/**
 * Close Portfolio Dialog
 * TASK-210: Close portfolio when losing client mandate
 *
 * Confirms portfolio closure with reason tracking
 */

import React, { useState } from 'react';
import { X, AlertTriangle, Archive } from 'lucide-react';
import { toast } from 'sonner';

interface ClosePortfolioDialogProps {
  isOpen: boolean;
  portfolioName: string;
  portfolioType: 'manual' | 'api';
  onClose: () => void;
  onConfirm: (reason: string, notes: string) => void;
}

const ClosePortfolioDialog: React.FC<ClosePortfolioDialogProps> = ({
  isOpen,
  portfolioName,
  portfolioType,
  onClose,
  onConfirm
}) => {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const closeReasons = [
    { value: 'mandate_lost', label: 'Lost Client Mandate' },
    { value: 'client_moved', label: 'Client Moved to Another Advisor' },
    { value: 'portfolio_consolidated', label: 'Portfolio Consolidated' },
    { value: 'account_closed', label: 'Account Closed by Client' },
    { value: 'relationship_ended', label: 'Client Relationship Ended' },
    { value: 'other', label: 'Other Reason' }
  ];

  const handleConfirm = () => {
    if (!reason) {
      toast.error('Please select a reason for closing');
      return;
    }

    onConfirm(reason, notes);

    // Reset form
    setReason('');
    setNotes('');
  };

  const handleCancel = () => {
    setReason('');
    setNotes('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Close Portfolio</h2>
              <p className="text-sm text-gray-500">{portfolioName}</p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-4">
          {/* Warning Box */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Archive className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-orange-900 mb-2">
                  What happens when you close this portfolio?
                </p>
                <ul className="space-y-1 text-orange-800">
                  <li>• Portfolio will be marked as "Closed"</li>
                  <li>• Won't appear in active portfolio views</li>
                  <li>• All data and history will be preserved</li>
                  {portfolioType === 'api' && (
                    <li>• Automatic syncing will be disabled</li>
                  )}
                  <li>• You can reopen it later if needed</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Reason Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Closing *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">Select a reason...</option>
              {closeReasons.map(r => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              placeholder="Add any additional context or notes about why this portfolio is being closed..."
            />
            <p className="mt-1 text-xs text-gray-500">
              This information will be saved for compliance and audit purposes
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              <span className="font-medium">Tip:</span> If you just want to stop syncing temporarily,
              use the Edit option to disable auto-sync instead of closing the portfolio.
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
          >
            Close Portfolio
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClosePortfolioDialog;
